import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';

type TemplateKey =
  | 'confirmation'
  | 'invoice'
  | 'reminder'
  | 'payment_pending'
  | 'driver_assignment'
  | 'driver_reminder'
  | 'bus_driver';

const WhatsAppTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeKey, setActiveKey] = useState<TemplateKey>('payment_pending');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => apiService.getWhatsAppTemplates(),
  });

  useEffect(() => {
    if (data?.templates) {
      setDrafts({ ...data.templates });
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (templates: Record<string, string>) =>
      apiService.updateWhatsAppTemplates(templates),
    onSuccess: (result) => {
      setSuccess('تم حفظ قوالب الرسائل');
      setError('');
      if (result?.templates) setDrafts({ ...result.templates });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-templates'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
    },
    onError: (err: any) => {
      setSuccess('');
      setError(err?.response?.data?.message || 'فشل حفظ القوالب');
    },
  });

  const keys = useMemo(() => {
    const meta = data?.meta || {};
    return (Object.keys(meta) as TemplateKey[]).length
      ? (Object.keys(meta) as TemplateKey[])
      : ([
          'payment_pending',
          'confirmation',
          'invoice',
          'reminder',
          'driver_assignment',
          'driver_reminder',
          'bus_driver',
        ] as TemplateKey[]);
  }, [data]);

  const bookingKeys = keys.filter((k) => data?.meta?.[k]?.group !== 'driver');
  const driverKeys = keys.filter((k) => data?.meta?.[k]?.group === 'driver');

  const resetActiveToDefault = () => {
    const def = data?.defaults?.[activeKey];
    if (def) {
      setDrafts((prev) => ({ ...prev, [activeKey]: def }));
    }
  };

  const handleSave = () => {
    setSuccess('');
    setError('');
    saveMutation.mutate(drafts);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <span className="mr-3 text-gray-600">جاري تحميل القوالب...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">قوالب رسائل واتساب</h1>
              <p className="text-gray-600 mt-1 text-sm">
                عدّل نص الرسائل المرسلة للعملاء والسائقين. استخدم المتغيرات بين قوسين.
              </p>
            </div>
            <Link
              to="/vendor/settings"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowRightIcon className="w-4 h-4" />
              العودة للإعدادات
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-emerald-50 text-emerald-800 px-4 py-3 text-sm">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white shadow rounded-lg p-4 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-2">حجز / عميل</h2>
              <div className="space-y-1">
                {bookingKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveKey(key)}
                    className={`w-full text-right px-3 py-2 rounded-md text-sm ${
                      activeKey === key
                        ? 'bg-emerald-50 text-emerald-800 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {data?.meta?.[key]?.label || key}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-2">سائق / باص</h2>
              <div className="space-y-1">
                {driverKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveKey(key)}
                    className={`w-full text-right px-3 py-2 rounded-md text-sm ${
                      activeKey === key
                        ? 'bg-emerald-50 text-emerald-800 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {data?.meta?.[key]?.label || key}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-gray-100">
              <h3 className="text-xs font-semibold text-gray-500 mb-2">متغيرات شائعة</h3>
              <ul className="text-xs text-gray-600 space-y-1 max-h-48 overflow-y-auto">
                {(data?.placeholders || []).slice(0, 14).map((p) => (
                  <li key={p.key} className="font-mono dir-ltr text-right">
                    <span className="text-emerald-700">{p.key}</span>
                    <span className="text-gray-400 mx-1">—</span>
                    <span className="font-sans">{p.desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white shadow rounded-lg p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {data?.meta?.[activeKey]?.label || activeKey}
              </h2>
              {data?.meta?.[activeKey]?.hint && (
                <p className="text-sm text-gray-500 mt-1">{data.meta[activeKey].hint}</p>
              )}
            </div>

            <textarea
              value={drafts[activeKey] || ''}
              onChange={(e) =>
                setDrafts((prev) => ({ ...prev, [activeKey]: e.target.value }))
              }
              rows={18}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              dir="rtl"
            />

            <div className="flex flex-wrap gap-2 justify-between">
              <button
                type="button"
                onClick={resetActiveToDefault}
                className="btn-secondary text-sm"
              >
                استعادة الافتراضي لهذا القالب
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {saveMutation.isPending ? 'جاري الحفظ...' : 'حفظ كل القوالب'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplates;
